import React, { useId, useState } from 'react';
import type { MarketDataRecord } from '../../services/marketDataService';
import type { MarketGroup, MarketGroupingMode } from '../../utils/marketGroupUtils';
import { meetsLtiDisclosureMinimum } from '../../utils/ltiDisclosure';
import { formatLtiMillions, formatLtiRatio, summarizeLtiTargetFact } from '../../utils/ltiTargetFact';
import MarketDashboardGroupingSwitch from './MarketDashboardGroupingSwitch';
import MarketGroupLabelCell from './MarketGroupLabelCell';
import './LtiTargetFactDashboard.css';

type LtiTargetFactDashboardProps = {
  groups: MarketGroup[];
  recordsForGroup: (groupKey: string) => MarketDataRecord[];
  groupingMode: MarketGroupingMode;
  onGroupingModeChange: (mode: MarketGroupingMode) => void;
  groupingDisabled?: boolean;
};

const RELIABILITY_MARK: Record<string, { color: string; label: string }> = {
  Excellent: { color: '#073763', label: 'Excellent' },
  High: { color: '#1155cc', label: 'High' },
  Moderate: { color: '#6d9eeb', label: 'Moderate' },
  Low: { color: '#999999', label: 'Low' },
};

function reliabilityMark(score: number): { color: string; label: string } {
  if (score > 0.8) return RELIABILITY_MARK.Excellent;
  if (score > 0.5) return RELIABILITY_MARK.High;
  if (score > 0.3) return RELIABILITY_MARK.Moderate;
  return RELIABILITY_MARK.Low;
}

function mixChannel(from: number, to: number, t: number): number {
  return Math.round(from + (to - from) * t);
}

function ratioTone(ratio: number): { color: string; background: string } {
  const pct = Math.round(ratio * 100);
  if (pct <= 0) return { color: '#8b95a1', background: 'transparent' };
  if (pct >= 100) return { color: '#157a3a', background: '#e3f7ea' };
  const t = pct / 100;
  return {
    color: `rgb(${mixChannel(109, 23, t)}, ${mixChannel(143, 128, t)}, ${mixChannel(120, 61, t)})`,
    background: 'transparent',
  };
}

function barWidth(value: number, scale: number): string {
  if (value <= 0 || scale <= 0) return '0%';
  return `${Math.min(100, (value / scale) * 100)}%`;
}

const LtiTargetFactDashboard: React.FC<LtiTargetFactDashboardProps> = ({
  groups,
  recordsForGroup,
  groupingMode,
  onGroupingModeChange,
  groupingDisabled = false,
}) => {
  const titleId = useId();
  const tipId = useId();
  const [tipOpen, setTipOpen] = useState(false);

  const rows = groups.map((group) => {
    const records = recordsForGroup(group.key);
    return {
      group,
      records,
      summary: summarizeLtiTargetFact(records),
      disclosed: meetsLtiDisclosureMinimum(records),
    };
  });
  const scale = rows.reduce(
    (max, row) => (
      row.disclosed ? Math.max(max, row.summary.targetAvg, row.summary.factAvg) : max
    ),
    0,
  );

  return (
    <section className="lti-compare" aria-labelledby={titleId}>
      <div className="lti-compare__head">
        <div>
          <h3 className="lti-compare__title" id={titleId}>
            Сравнение целевого и фактически выплаченного LTI
            <span className="lti-compare__info">
              <button
                type="button"
                className="lti-compare__info-btn"
                aria-label="Как считаются Target и Fact"
                aria-describedby={tipOpen ? tipId : undefined}
                aria-expanded={tipOpen}
                onMouseEnter={() => setTipOpen(true)}
                onMouseLeave={() => setTipOpen(false)}
                onFocus={() => setTipOpen(true)}
                onBlur={() => setTipOpen(false)}
              >
                i
              </button>
              {tipOpen && (
                <p className="lti-compare__tip" id={tipId} role="tooltip">
                  Оба значения — годовой эквивалент. Target — цель, Fact — выплата.
                  В строке среднее по всем датакейсам группы; если LTI нет, в среднее входит 0.
                </p>
              )}
            </span>
          </h3>
          <p className="lti-compare__subtitle">За последний закрытый отчетный период</p>
          {groupingMode === 'cluster' && (
            <p className="lti-compare__subtitle">
              Строки объединяют записи семейства с грейдами из диапазона кластера.
            </p>
          )}
        </div>
        <div className="lti-compare__tools">
          <MarketDashboardGroupingSwitch
            mode={groupingMode}
            onChange={onGroupingModeChange}
            disabled={groupingDisabled}
          />
          <div className="lti-compare__legend" aria-hidden="true">
            <span className="lti-compare__legend-item">
              <span className="lti-compare__swatch lti-compare__swatch--target" />
              Target LTI
            </span>
            <span className="lti-compare__legend-item">
              <span className="lti-compare__swatch lti-compare__swatch--fact" />
              Fact LTI
            </span>
          </div>
        </div>
      </div>

      <div className="lti-compare__card">
        <table className="lti-compare__table">
          <thead>
            <tr>
              <th className="lti-compare__col-grade" scope="col">Грейд / Карьерный уровень</th>
              <th className="lti-compare__col-reliability" scope="col">Надежность<br />данных</th>
              <th className="lti-compare__col-count" scope="col">Кол-во<br />датакейсов</th>
              <th scope="col">Target LTI</th>
              <th scope="col">Fact LTI</th>
              <th scope="col">Fact / Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ group, summary, disclosed }) => {
              const mark = reliabilityMark(summary.avgReliability);
              const tone = ratioTone(summary.ratio);
              return (
                <tr key={group.key}>
                  <td className="lti-compare__grade">
                    <MarketGroupLabelCell group={group} />
                  </td>
                  <td className="lti-compare__reliability-cell">
                    <div className="lti-compare__reliability">
                      <span
                        className="lti-compare__reliability-mark"
                        style={{ backgroundColor: mark.color }}
                      />
                      <span className="lti-compare__reliability-label">{mark.label}</span>
                    </div>
                  </td>
                  <td className="lti-compare__count">{summary.count}</td>
                  <td>
                    {disclosed ? (
                      <div className="lti-compare__meter">
                        <span className="lti-compare__track" aria-hidden="true">
                          <span
                            className="lti-compare__fill lti-compare__fill--target"
                            style={{
                              width: barWidth(summary.targetAvg, scale),
                              minWidth: summary.targetAvg > 0 ? 2 : 0,
                            }}
                          />
                        </span>
                        <span className="lti-compare__amount">{formatLtiMillions(summary.targetAvg)}</span>
                      </div>
                    ) : <span className="lti-compare__withheld">—</span>}
                  </td>
                  <td>
                    {disclosed ? (
                      <div className="lti-compare__meter">
                        <span className="lti-compare__track" aria-hidden="true">
                          <span
                            className="lti-compare__fill lti-compare__fill--fact"
                            style={{
                              width: barWidth(summary.factAvg, scale),
                              minWidth: summary.factAvg > 0 ? 2 : 0,
                            }}
                          />
                        </span>
                        <span className="lti-compare__amount">{formatLtiMillions(summary.factAvg)}</span>
                      </div>
                    ) : <span className="lti-compare__withheld">—</span>}
                  </td>
                  <td className="lti-compare__ratio">
                    {disclosed ? (
                      <span
                        className="lti-compare__ratio-value"
                        style={{ color: tone.color, backgroundColor: tone.background }}
                      >
                        {formatLtiRatio(summary.ratio)}
                      </span>
                    ) : <span className="lti-compare__withheld">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default LtiTargetFactDashboard;
